import { useState } from 'react';
import { Form, Table, Popover, Row, Input, Empty } from 'antd';
import { Plus, Pencil, Trash, ThreeDotsVertical } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { FormField, FormTextArea, FormAttachment } from '../../../components/form/FormBasic';
import { FormFieldTypeEnum } from '../../../enums/FormFieldTypeEnum';
import { FileTypeEnum } from '../../../enums/FileTypeEnum';
import FormModelComponent from '../../../components/model/FormModelComponent';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { ActionMenu } from '../../../components/common/ActionMenu';
import { XSquare } from 'react-bootstrap-icons';
import {
  useGetNews,
  useCreateNews,
  useUpdateNews,
  useDeleteNews,
} from '../../../api/hooks/useNewsQuery';
import { NewsItem, CreateNewsDto, UpdateNewsDto } from '../../../api/services/newsService';

interface FileObject {
  image: string;
  dataUrl: string;
  id: string;
  name: string;
  size: number;
  type: string;
}
import { useNotificationContext } from '../../../contexts/NotificationContext';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { mapUrlsToAttachmentField } from '../../../utils/mapUrlsToAttachmentField';
import { useWatch } from 'antd/es/form/Form';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../constants/regex';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { usePermissions } from '../../../hooks/usePermissions';

const NewsManagement = () => {
  const { t } = useTranslation(['administration']);
  const { successNotification, errorNotification } = useNotificationContext();
  const { hasPermission } = usePermissions();
  const canManageNews = hasPermission(PermissionEnum.MANAGE_NEWS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingItem, setEditingItem] = useState<NewsItem | null>(null);
  const [loadTable, setLoadTable] = useState(false);

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [selectedNewsId, setSelectedNewsId] = useState<number>();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const { data: newsItems = [], isLoading, error } = useGetNews(1, 100);
  const createNewsMutation = useCreateNews();
  const updateNewsMutation = useUpdateNews();
  const deleteNewsMutation = useDeleteNews();

  const imageValue = useWatch('imageVal', form);

  const { isOnline } = useIsOnlineContext();

  const handleAddNews = () => {
    if (!canManageNews) return;

    setEditingItem(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEditNews = (item: NewsItem) => {
    if (!canManageNews) return;

    setEditingItem(item);
    const imageConverted = mapUrlsToAttachmentField([item?.preview || '']);

    /* let previewValue: FileObject[] = [];
    if (item.preview) {
      previewValue = [
        {
          image: item.preview,
          dataUrl: item.preview,
          id: `existing-${item.id}`,
          name: 'existing-preview.jpg',
          size: 0,
          type: 'image/jpeg',
        },
      ];
    } */

    form.setFieldsValue({
      title: item.title,
      summary: item.summary,
      description: item.description,
      imageVal: imageConverted,
    });
    setIsModalOpen(true);
  };

  const handleDeleteNews = async () => {
    if (!canManageNews || !selectedNewsId) return;

    try {
      setLoadTable(true);
      setIsDeleteConfirmationOpen(false);
      await deleteNewsMutation.mutateAsync(selectedNewsId);
      successNotification(t('newsAlertDeleteSuccess'));
    } catch {
      setIsDeleteConfirmationOpen(false);
      errorNotification(t('newsAlertDeleteFail'));
    } finally {
      setLoadTable(false);
    }
  };

  const cancelDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const getDeleteConfirmation = (id: number) => {
    if (!canManageNews) return;

    setIsDeleteConfirmationOpen(true);
    setSelectedNewsId(id);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setEditingItem(null);
  };

  const handleSubmit = async () => {
    if (!canManageNews) return;

    const values = await form.validateFields();

    let previewBase64 = '';
    if (values.preview && values.preview.length > 0) {
      const previewFile = values.preview[0];
      previewBase64 = previewFile?.image || previewFile?.dataUrl || previewFile || '';
    }
    try {
      if (editingItem) {
        const updateData: UpdateNewsDto = {
          title: values.title,
          summary: values.summary,
          description: values.description,
          preview: previewBase64,
        };
        handleModalCancel();
        setLoadTable(true);
        await updateNewsMutation.mutateAsync({ id: editingItem.id, data: updateData });
        successNotification(t('newsAlertUpdateSuccess'));
      } else {
        const createData: CreateNewsDto = {
          title: values.title,
          summary: values.summary,
          description: values.description,
          preview: previewBase64,
        };
        handleModalCancel();
        setLoadTable(true);
        await createNewsMutation.mutateAsync(createData);
        successNotification(t('newsAlertCreateSuccess'));
      }

      // handleModalCancel();
    } catch {
      errorNotification(t('newsAlertFailedToSave'));
    } finally {
      setLoadTable(false);
    }
  };

  const columns = [
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: t('summary'),
      dataIndex: 'summary',
      key: 'summary',
      width: 250,
      ellipsis: true,
    },
    {
      title: t('createdAt'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (date: number) => new Date(Number(date)).toLocaleDateString(),
    },
    ...(canManageNews
      ? [
          {
            title: t('action'),
            dataIndex: 'action',
            key: 'action',
            width: 100,
            align: 'center' as const,
            render: (_: unknown, record: NewsItem) => {
              const menuItems = [
                {
                  text: t('edit'),
                  icon: <Pencil size={16} className="text-primary-blue-icon" />,
                  click: () => handleEditNews(record),
                },
                {
                  text: t('delete'),
                  icon: <Trash size={16} className="text-primary-blue-icon" />,
                  click: () => getDeleteConfirmation(record.id),
                },
              ];

              return (
                <Popover
                  content={<ActionMenu menuItems={menuItems} />}
                  className="cursor-pointer"
                  placement="bottom"
                  trigger="hover"
                  overlayStyle={{ zIndex: 800 }}
                >
                  <Row justify="center">
                    <ThreeDotsVertical size={15} />
                  </Row>
                </Popover>
              );
            },
          },
        ]
      : []),
  ];

  if (!canManageNews) {
    return null;
  }

  return (
    <>
      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleDeleteNews,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDeleteConfirmation,
          },
        }}
        styleClasses="z-100 p-6"
      />

      <div className="mt-6 w-full">
      <div className="mb-6 flex justify-end">
        <PrimarySquareBtn handleOnClick={handleAddNews} disabled={!isOnline}>
          <div className="flex">
            <Plus className="h-6 w-6 text-white" />
            <p className="pt-1">{t('addNews')}</p>
          </div>
        </PrimarySquareBtn>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4 text-red-700">
          {t('errorLoadingNewsArticles')}
        </div>
      )}

      <div className="mb-6 w-full overflow-x-auto">
        <Table
          dataSource={Array.isArray(newsItems) ? newsItems : []}
          columns={columns}
          rowKey="id"
          loading={
            isLoading ||
            deleteNewsMutation.isPending ||
            createNewsMutation.isPaused ||
            updateNewsMutation.isPending ||
            loadTable
          }
          size="middle"
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            position: ['bottomLeft'],
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total}`,
            locale: {
              jump_to: t('goTo'),
              page: t('page'),
              items_per_page: `/ ${t('page')}`,
            },
            onChange: (page: number, size?: number) => {
              setCurrentPage(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            },
          }}
          locale={{
            emptyText: (
              <Empty
                className="bg-gray-bg-color py-5"
                description={newsItems?.length === 0 ? t('noData') : null}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          scroll={{ x: 800 }}
        />
      </div>

      <FormModelComponent
        isOpen={isModalOpen}
        header={editingItem ? t('editNews') : t('addNews')}
        content={
          <Form layout="vertical" form={form} name="newsForm" className="space-y-4">
            <FormField
              name="title"
              label={t('title')}
              rules={[
                { required: true, message: t('titleRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('titleSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(String(value))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('titleMinLength'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('titleMaxLength'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              placeholder={t('titlePlaceholder')}
              type={FormFieldTypeEnum.INPUT}
              isRequired={true}
              // maxLength={100}
            />

            <FormField
              name="summary"
              label={t('summary')}
              rules={[
                { required: true, message: t('summaryRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('summarySpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(String(value))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('summaryMinLength'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 200) {
                      return Promise.reject(t('summaryMaxLength'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              placeholder={t('summaryPlaceholder')}
              type={FormFieldTypeEnum.INPUT}
              isRequired={true}
            />

            <FormTextArea
              name="description"
              label={t('description')}
              rules={[
                { required: true, message: t('descriptionRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('descriptionSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(String(value))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 1) {
                      return Promise.reject(t('descriptionMinLength'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 1000) {
                      return Promise.reject(t('descriptionMaxLength'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              placeholder={t('descriptionPlaceholder')}
              isRequired={false}
            />

            <FormAttachment
              name="preview"
              label={t('image')}
              isRequired={true}
              form={form}
              maxCount={1}
              fileTypes={[FileTypeEnum.PNG, FileTypeEnum.JPEG, FileTypeEnum.JPG]}
              validationMessage={t('previewRequired')}
              initialFiles={imageValue ? imageValue : []}
            />

            <Form.Item name="imageVal" label="News Image" hidden>
              <Input hidden />
            </Form.Item>
          </Form>
        }
        onClickedCancel={handleModalCancel}
        footerAction={{
          confimation: {
            label: editingItem ? t('update') : t('create'),
            onClick: handleSubmit,
            isLoading: createNewsMutation.isPending || updateNewsMutation.isPending,
            isDisabled: createNewsMutation.isPending || updateNewsMutation.isPending,
          },
          cancel: {
            label: t('cancel'),
            onClick: handleModalCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      />
      </div>
    </>
  );
};

export default NewsManagement;
